/**
 * Dashboard Service
 * Handles all API calls for the operational dashboard
 */
import axios from 'axios'

const BASE_URL = '/back-office'

// Use the global axios instance instead of a local one to benefit from CSRF configuration in bootstrap.js
const http = window.axios || axios

/**
 * Get dashboard statistics
 * @param {string} period - Period filter (today, tomorrow, yesterday, seven_days, monthly, yearly)
 * @returns {Promise} Statistics data
 */
export const getStats = async (period = 'today') => {
    const response = await http.post(`${BASE_URL}/admin-home-stats`, {
        type: period,
    })
    return response.data
}

/**
 * Get reservations list
 * @param {string} period - Period filter
 * @param {number} page - Page number (default: 1)
 * @param {number} perPage - Items per page (default: 15)
 * @returns {Promise} Reservations data
 */
export const getReservations = async (period = 'today', page = 1, perPage = 15) => {
    const response = await http.post(`${BASE_URL}/admin-home-reservations`, {
        type: period,
        page: page,
        perPage: perPage,
    })
    return response.data
}

/**
 * Get orders list
 * @param {string} period - Period filter
 * @param {number} page - Page number (default: 1)
 * @param {number} perPage - Items per page (default: 15)
 * @returns {Promise} Orders data
 */
export const getOrders = async (period = 'today', page = 1, perPage = 15) => {
    const response = await http.post(`${BASE_URL}/admin-home-orders`, {
        type: period,
        page: page,
        perPage: perPage,
    })
    return response.data
}

/**
 * Get weather forecast via backend proxy (avoids CORS)
 * @param {string} city - City name (ignored — backend uses establishment city)
 * @param {string} apiKey - Ignored (backend manages API key)
 * @param {string} lang - Language code
 * @returns {Promise} Forecast data (OpenWeatherMap format)
 */
export const getWeather = async (city = 'Paris', apiKey = null, lang = 'fr') => {
    try {
        const response = await http.get(`${BASE_URL}/statistiques/weather/forecast`, {
            params: { lang },
        })
        return response.data?.data || null
    } catch (error) {
        console.error('Error fetching weather forecast:', error)
        return null
    }
}

/**
 * Get current weather via backend proxy (avoids CORS)
 * @param {string} city - City name (ignored — backend uses establishment city)
 * @param {string} apiKey - Ignored (backend manages API key)
 * @param {string} lang - Language code
 * @returns {Promise} Current weather data (OpenWeatherMap format)
 */
export const getCurrentWeather = async (city = 'Paris', apiKey = null, lang = 'fr') => {
    try {
        const response = await http.get(`${BASE_URL}/statistiques/weather`, {
            params: { lang },
        })
        return response.data?.data || null
    } catch (error) {
        console.error('Error fetching current weather:', error)
        return null
    }
}

/**
 * Get reservation trends (today + next 3 open days)
 * @returns {Promise} Reservation trends data with hourly breakdown
 */
export const getReservationTrends = async () => {
    try {
        const response = await http.get(`${BASE_URL}/statistiques/reservation_trends`)
        return response.data
    } catch (error) {
        console.error('Error fetching reservation trends:', error)
        return null
    }
}

/**
 * Get all dashboard data at once
 * @param {string} period - Period filter
 * @returns {Promise} All dashboard data
 */
export const getAllDashboardData = async (period = 'today') => {
    const [stats, reservations, orders, weather] = await Promise.all([
        getStats(period).catch((e) => {
            console.error('Stats error:', e)
            return null
        }),
        getReservations(period).catch((e) => {
            console.error('Reservations error:', e)
            return null
        }),
        getOrders(period).catch((e) => {
            console.error('Orders error:', e)
            return null
        }),
        getWeather().catch((e) => {
            console.error('Weather error:', e)
            return null
        }),
    ])

    return { stats, reservations, orders, weather }
}

/**
 * Map weather code to icon name
 * @param {number} code - WMO weather code
 * @returns {string} Icon name
 */
export const getWeatherIcon = (code) => {
    if (code === 0) return 'sunny'
    if (code <= 3) return 'partly_cloudy'
    if (code <= 48) return 'cloudy'
    if (code <= 67) return 'rainy'
    if (code <= 77) return 'snowy'
    if (code <= 82) return 'shower'
    if (code >= 95) return 'thunderstorm'
    return 'cloudy'
}

export default {
    getStats,
    getReservations,
    getOrders,
    getWeather,
    getCurrentWeather,
    getReservationTrends,
    getAllDashboardData,
    getWeatherIcon,
}
